import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { usePageMeta } from "@/hooks/use-page-meta";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { isNativeApp } from "@/lib/platform";
import { getBaseUrl } from "@/lib/site";

const signInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type SignInFormData = z.infer<typeof signInSchema>;

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNative = isNativeApp();

  const openExternalSignup = () => {
    const url = `${getBaseUrl()}/signup`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  usePageMeta({
    title: "Sign in – Buukd",
    description: "Sign in to your Buukd account. Manage appointments & walk-ins for your business.",
    path: "/signin",
  });

  // If already signed in, skip this page.
  useEffect(() => {
    if (!loading && user) {
      navigate("/app", { replace: true });
    }
  }, [loading, user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
  });

  const onSubmit = async (data: SignInFormData) => {
    setIsLoading(true);
    const { error } = await signIn(data.email, data.password);
    setIsLoading(false);

    if (error) {
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      navigate("/app");
    }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col font-bricolage">
      <PublicHeader />

      <main
        className="public-auth-theme flex-1 overflow-y-auto bg-gradient-hero px-4 pb-6 pb-safe"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 4rem + 1.25rem)" }}
      >
        <div className="w-full max-w-md mx-auto">
          <div className="text-center mb-5">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Sign in to your account</h1>
            {isNative ? (
              <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                Manage your appointments &amp; walk-ins using Buukd.
              </p>
            ) : (
              <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                Don't have an account?{" "}
                <Link to="/signup" className="bg-gradient-to-br from-pink-400 to-pink-600 bg-clip-text text-transparent font-medium hover:underline animate-gradient-subtle">
                  Sign up
                </Link>
              </p>
            )}
          </div>

          <div className="bg-card rounded-2xl border border-border shadow-soft-lg p-5 sm:p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  {...register("email")}
                  className="h-11"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    {...register("password")}
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                <div className="flex items-center gap-2">
                  <Checkbox id="remember" />
                  <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                    Remember me
                  </Label>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm bg-gradient-to-br from-pink-400 to-pink-600 bg-clip-text text-transparent font-medium hover:underline animate-gradient-subtle"
                >
                  Forgot your password?
                </Link>
              </div>

              <Button
                type="submit"
                variant="gradient"
                className="w-full h-11"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </Button>
            </form>
          </div>

          {isNative && (
            <div className="mt-6 text-center space-y-3">
              <p className="text-sm text-muted-foreground">Don't have an account?</p>
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 border-pink-400 hover:bg-pink-50 dark:hover:bg-pink-950/20"
                onClick={openExternalSignup}
              >
                <span className="bg-gradient-to-br from-pink-400 to-pink-600 bg-clip-text text-transparent font-medium animate-gradient-subtle">
                  Sign up for free
                </span>
              </Button>
            </div>
          )}
        </div>
      </main>

      {!isNative && <PublicFooter />}
    </div>
  );
}
